import { Breadcrumb } from "@kazaar-platform/design-system";
import {
  RiAdminLine,
  RiBox3Line,
  RiFlashlightLine,
  RiLeafLine,
  RiSettings4Line,
  RiShoppingBagLine,
  RiStore2Line,
} from "@remixicon/react";
import { Link, Navigate, Outlet, Route } from "react-router-dom";
import { BreadcrumbInQueryCache } from "@/components/Breadcrumb";
import { RootErrorBoundary } from "@/components/RootErrorBoundary";
import { OrganizationBreadcrumb } from "@/pages/Admin/OrganizationPage/OrganizationBreadcrumb";
import { WorkspaceBreadcrumb } from "@/pages/Admin/WorkspacePage/WorkspaceBreadcrumb";
import { ModificationRequestBreadcrumb } from "@/pages/ModificationRequestPage/_partials/ModificationRequestBreadcrumb";
import { OrderItemBreadcrumb } from "@/pages/OrderItemPage/OrderItemBreadcrumb";
import { OrderBreadcrumb } from "@/pages/OrdersPage/OrderBreadcrumb";
import { tolgee } from "@/providers/TranslationProvider";
import { EcoScoringFormPage } from "../EcoScoringFormPage";
import { NewRequestPage } from "../NewRequestPage";
import { NewRequestPageBreadcrumb } from "../NewRequestPage/NewRequestPageBreadcrumb";
import { OpenBriefPage } from "../OpenBriefPage";
import { OrderDetailsAddQuoteFormPage } from "../OrderDetailsAddQuoteFormPage";
import { RetroplanningFormPage } from "../RetroplanningFormPage";
import { SettingsFormPage } from "../SettingsFormPage";
import { AppRouteLayout } from "./AppRouteLayout";
import {
  AccountPage,
  AdminAccountPage,
  AdminOrganizationPage,
  AdminRedirectPage,
  AdminWorkspacePage,
  CatalogMediumPage,
  CatalogPage,
  DashboardPage,
  InboxPage,
  ModificationRequestPage,
  OrderItemPage,
  OrderPage,
  OrderSuccessPage,
  OrdersPage,
} from "./lazyPages";

export const routes = (
  <Route
    key="client-routes"
    path="/"
    element={<AppRouteLayout />}
    ErrorBoundary={RootErrorBoundary}
  >
    <Route index element={<Navigate to="/dashboard" replace />} />

    <Route
      path="dashboard"
      element={<DashboardPage />}
      handle={{
        crumb: () => (
          <Link to="/dashboard" className="flex items-center gap-sp-base">
            <RiFlashlightLine className="size-sp-md" />
            {tolgee.t("navigation.dashboard")}
          </Link>
        ),
      }}
    />

    <Route path="inbox" element={<InboxPage />} />

    <Route
      path="account"
      element={<AccountPage />}
      handle={{
        crumb: () => (
          <Link to="/account">{tolgee.t("navigation.order.account")}</Link>
        ),
      }}
    />

    <Route path="shop" element={<Navigate to="/catalog" replace />} />

    <Route
      path="catalog"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/catalog" className="flex items-center gap-sp-base">
            <RiStore2Line className="size-sp-md" />
            {tolgee.t("navigation.order.catalog")}
          </Link>
        ),
      }}
    >
      <Route index element={<CatalogPage />} />
      <Route
        path=":id"
        element={<CatalogMediumPage />}
        loader={({ params }) => params}
        handle={{
          crumb: (data: { id: string }) => (
            <BreadcrumbInQueryCache
              cacheKey={["catalogMedium", data.id]}
              name="data.medium.name"
            />
          ),
        }}
      />
    </Route>

    <Route
      path="orders"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/orders" className="flex items-center gap-sp-base">
            <RiShoppingBagLine className="size-sp-md" />
            {tolgee.t("navigation.order.myOrders")}
          </Link>
        ),
      }}
    >
      <Route index element={<OrdersPage />} />
      <Route path="success" element={<OrderSuccessPage />} />
      <Route
        path=":orderId"
        element={<Outlet />}
        loader={({ params }) => params}
        handle={{
          crumb: (data: { orderId: string }) => (
            <OrderBreadcrumb orderId={data.orderId} />
          ),
        }}
      >
        <Route index element={<OrderPage />} />
        <Route
          path="order-items/:orderItemId"
          element={<Outlet />}
          loader={({ params }) => params}
          handle={{
            crumb: (data: { orderItemId: string; orderId: string }) => (
              <OrderItemBreadcrumb
                orderId={data.orderId}
                orderItemId={data.orderItemId}
              />
            ),
          }}
        >
          <Route index element={<OrderItemPage />} />
          <Route
            path="edit"
            element={<ModificationRequestPage />}
            loader={({ params }) => params}
            handle={{
              crumb: (data: { orderItemId: string; orderId: string }) => (
                <ModificationRequestBreadcrumb
                  orderId={data.orderId}
                  orderItemId={data.orderItemId}
                />
              ),
            }}
          />
          <Route
            path="new-request"
            element={<NewRequestPage />}
            loader={({ params }) => params}
            handle={{
              crumb: (data: { orderItemId: string; orderId: string }) => (
                <NewRequestPageBreadcrumb
                  orderId={data.orderId}
                  orderItemId={data.orderItemId}
                />
              ),
            }}
          />
          <Route path="add-quote" element={<OrderDetailsAddQuoteFormPage />} />
          <Route path="add-retroplanning" element={<RetroplanningFormPage />} />
        </Route>
      </Route>
    </Route>

    <Route
      path="open-brief"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/open-brief" className="flex items-center gap-sp-base">
            <RiBox3Line className="size-sp-md" />
            {tolgee.t("navigation.openBrief")}
            <Breadcrumb.Tag color="primary" variant="alpha">
              {tolgee.t("navigation.beta")}
            </Breadcrumb.Tag>
          </Link>
        ),
      }}
    >
      <Route index element={<OpenBriefPage />} />
    </Route>

    <Route
      path="settings"
      element={<SettingsFormPage />}
      handle={{
        crumb: () => (
          <Link to="/settings" className="flex items-center gap-sp-base">
            <RiSettings4Line className="size-sp-md" />
            {tolgee.t("oms.featureFlags.title")}
          </Link>
        ),
      }}
    />

    <Route
      path="eco-scoring"
      element={<EcoScoringFormPage />}
      handle={{
        crumb: () => (
          <Link to="/eco-scoring" className="flex items-center gap-sp-base">
            <RiLeafLine className="size-sp-md" />
            {tolgee.t("oms.updateEcoScore.title")}
          </Link>
        ),
      }}
    />

    <Route
      path="admin"
      element={<Outlet />}
      handle={{
        crumb: () => (
          <Link to="/admin" className="flex items-center gap-sp-base">
            <RiAdminLine className="size-sp-md" />
            {tolgee.t("navigation.admin")}
          </Link>
        ),
      }}
    >
      <Route index element={<AdminRedirectPage />} />
      <Route path="account/:accountId" element={<AdminAccountPage />} />
      <Route
        path="account/:accountId/organization/:organizationId"
        loader={({ params }) => params}
        handle={{
          crumb: (data: { accountId: string; organizationId: string }) => (
            <OrganizationBreadcrumb
              accountId={data.accountId}
              organizationId={data.organizationId}
            />
          ),
        }}
      >
        <Route index element={<AdminOrganizationPage />} />
        <Route
          path="workspace/:workspaceId"
          element={<AdminWorkspacePage />}
          loader={({ params }) => params}
          handle={{
            crumb: (data: { accountId: string; workspaceId: string }) => (
              <WorkspaceBreadcrumb
                accountId={data.accountId}
                workspaceId={data.workspaceId}
              />
            ),
          }}
        />
      </Route>
    </Route>
  </Route>
);
